import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Us - Cultaz | Hybrid Retail Management Software for Nigerian Retailers</title>
        <meta name="description" content="Learn about Cultaz - hybrid retail management software designed for Nigerian retailers. Combining cloud convenience with offline reliability. POS, inventory, reporting, and more." />
        <meta name="keywords" content="about Cultaz, retail software company, Nigerian retail technology, POS software company, inventory management company, retail solutions Nigeria" />
        <meta name="author" content="Cultaz" />
        <meta property="og:title" content="About Us - Cultaz | Retail Management Software" />
        <meta property="og:description" content="Learn about Cultaz - hybrid retail management software for Nigerian retailers. Cloud and offline POS solutions." />
        <meta property="og:image" content="https://cultaz.com/banners/1.png" />
        <meta property="og:url" content="https://cultaz.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cultaz" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Us - Cultaz" />
        <meta name="twitter:description" content="Learn about Cultaz - hybrid retail management software for Nigerian retailers." />
        <meta name="twitter:image" content="https://cultaz.com/banners/1.png" />
        <link rel="canonical" href="https://cultaz.com/about" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-secondary/5 py-16 md:py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-6 mix-blend-overlay"></div>
          <div className="container mx-auto px-6 lg:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">About Cultaz</h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Cultaz provides hybrid retail management tools for Nigerian retailers - combining cloud convenience with offline reliability. Our platform includes POS, inventory, reporting, and integrations to help you run and grow your business.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16 md:py-20 lg:py-24 bg-background">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                <div className="p-6 bg-card rounded-xl border border-border">
                  <h3 className="font-semibold mb-3 text-xl">Mission</h3>
                  <p className="text-sm text-muted-foreground">Make simple, reliable point-of-sale and e-commerce tools that let any merchant run and grow their business.</p>
                </div>
                <div className="p-6 bg-card rounded-xl border border-border">
                  <h3 className="font-semibold mb-3 text-xl">Vision</h3>
                  <p className="text-sm text-muted-foreground">A world where merchants can focus on customers, not technology - online or offline.</p>
                </div>
                <div className="p-6 bg-card rounded-xl border border-border">
                  <h3 className="font-semibold mb-3 text-xl">How It Helps</h3>
                  <p className="text-sm text-muted-foreground">Fast local performance for cashiers, easy online store setup, and optional cloud sync to keep website and store catalogs aligned.</p>
                </div>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Our Platform at a Glance</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="p-6 bg-card rounded-xl border border-border"
                >
                  <h3 className="font-semibold mb-2">Cloud Dashboard</h3>
                  <p className="text-sm text-muted-foreground mb-4">Real-time insights to help you make data-driven decisions from anywhere.</p>
                  <img
                    src="/cloud-app-screenshot/dashboard-light.png"
                    alt="Cultaz Cloud Dashboard"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="p-6 bg-card rounded-xl border border-border"
                >
                  <h3 className="font-semibold mb-2">Point of Sale</h3>
                  <p className="text-sm text-muted-foreground mb-4">Fast, reliable checkout with receipt printing and barcode support.</p>
                  <img
                    src="/cloud-app-screenshot/pos-light.png"
                    alt="Cultaz POS Interface"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="p-6 bg-card rounded-xl border border-border"
                >
                  <h3 className="font-semibold mb-2">Inventory Management</h3>
                  <p className="text-sm text-muted-foreground mb-4">Track stock levels, manage products, and automate reordering.</p>
                  <img
                    src="/cloud-app-screenshot/add-product-light.png"
                    alt="Cultaz Product Management"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="p-6 bg-card rounded-xl border border-border"
                >
                  <h3 className="font-semibold mb-2">Analytics & Reports</h3>
                  <p className="text-sm text-muted-foreground mb-4">Comprehensive business analytics and detailed sales reports.</p>
                  <img
                    src="/cloud-app-screenshot/analytics-light.png"
                    alt="Cultaz Analytics Dashboard"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="p-6 bg-card rounded-xl border border-border"
                >
                  <h3 className="font-semibold mb-2">Desktop POS</h3>
                  <p className="text-sm text-muted-foreground mb-4">Offline-first desktop application for uninterrupted operations.</p>
                  <img
                    src="/desktop-app-screenshots/UI1.png"
                    alt="Cultaz Desktop POS"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  className="p-6 bg-card rounded-xl border border-border"
                >
                  <h3 className="font-semibold mb-2">Settings & Configuration</h3>
                  <p className="text-sm text-muted-foreground mb-4">Flexible settings and user management controls.</p>
                  <img
                    src="/cloud-app-screenshot/settings-light.png"
                    alt="Cultaz Settings"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </motion.div>
              </div>

              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl p-8 md:p-12 border border-primary/20">
                <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">Why Nigerian Retailers Choose Cultaz</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold mb-2">Comprehensive Dashboard</h3>
                    <p className="text-sm text-muted-foreground">Real-time insights to help you make data-driven decisions.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Point of Sale</h3>
                    <p className="text-sm text-muted-foreground">Fast, reliable checkout with receipt printing and barcode support.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Customer Management</h3>
                    <p className="text-sm text-muted-foreground">Manage customers, loyalty, and purchase history in one place.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Offline Reliability</h3>
                    <p className="text-sm text-muted-foreground">Work without internet with our desktop POS application.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
    </>
  );
};

export default About;
